import React, { useRef } from "react";
import { useEffect, useState } from "react";
import { parseDocument } from "yaml";
import {
  FieldSelectorProps,
  QueryBuilder,
  Rule,
  RuleGroupType,
  ValueEditorProps,
} from "react-querybuilder";
import "react-querybuilder/dist/query-builder.css";
import yamlToJsonNew, {
  convertToQueryBuilderFormat,
  collapseBusinessHourWindow,
  expandEmailBusinessHours,
  BUSINESS_HOUR_FIELD,
  DEFAULT_BUSINESS_HOUR_END,
  DEFAULT_BUSINESS_HOUR_START,
  formatRules,
  parseBusinessHourValue,
} from "./yamlParser";
import saveAssetRules from "./saveAssetRules";
import Utility from "../../../helpers/Utility";
import ruleGeneratorService from "../../../services/ruleGeneratorService";

interface Rule {
  code: string;
  status: string;
  deploy: string;
  description: string;
  summary: string;
  recommended_action: string;
  prerequisite?: any;
  trigger_pre?: any;
  trigger_post?: any;
  email_condition?: any;
  link?: string;
}

interface RulesData {
  anchors: any[];
  rules: Rule[];
}

const operators = [
  { name: "==", label: "==" }, // name -> output yaml, label -> display
  { name: "!=", label: "!=" },
  { name: "<", label: "<" },
  { name: ">", label: ">" },
  { name: "<=", label: "<=" },
  { name: ">=", label: ">=" },
];

const defaultPostConditionFields = [
  { name: "_state.consecutive_count", label: "Consecutive Count" },
  { name: "_state.stay_mins", label: "Condition Stay(in mins)" },
];
const postConditionOperators = [
  { name: ">", label: ">" },
  { name: ">=", label: ">=" },
];

const defaultPrerequisiteFields = [
  { name: "_sys_attr.rv_mode", label: "RV Mode" },
  { name: "_sys_attr.heat_provided_by", label: "Heat Provided By" },
  { name: "_sys_attr.refrigerant", label: "Refrigerant" },
  { name: "_sys_attr.r_switch_condition", label: "R Switch Condition" },
];
const prerequisiteOperators = [{ name: "==", label: "==" }];

const defaultEmailFields = [
  { name: BUSINESS_HOUR_FIELD, label: "Business Hour" },
  { name: "_state.in_business_hour", label: "In Business Hour" },
  {
    name: "_state.email_elapsed_mins",
    label: "Wait (in mins) before sending next Email",
  },
];
const emailOperators = [
  { name: "==", label: "==" },
  { name: ">", label: ">" },
  { name: ">=", label: ">=" },
  { name: "<", label: "<" },
  { name: "<=", label: "<=" },
];
const businessHourOperator = [{ name: "between", label: "between" }];
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => hour);

const hourOptionLabel = (hour: number) => {
  const padded = String(hour).padStart(2, "0");
  const suffix =
    hour === 0 ? "12 AM" : hour === 12 ? "12 PM" : hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
  return `${padded} (${suffix})`;
};

const EmailValueEditor: ValueEditorProps["component"] = (props) => {
  const { field, value, handleOnChange } = props;

  if (field === BUSINESS_HOUR_FIELD) {
    const hours = parseBusinessHourValue(value);
    return (
      <span className="business-hour-editor">
        <label className="business-hour-label">START: </label>
        <select
          className="rule-value business-hour-select"
          value={hours.start}
          onChange={(event) =>
            handleOnChange({
              start: Number(event.target.value),
              end: hours.end,
            })
          }
        >
          {HOUR_OPTIONS.map((hour) => (
            <option key={`start-${hour}`} value={hour}>
              {hourOptionLabel(hour)}
            </option>
          ))}
        </select>
        <label className="business-hour-label">END: </label>
        <select
          className="rule-value business-hour-select"
          value={hours.end}
          onChange={(event) =>
            handleOnChange({
              start: hours.start,
              end: Number(event.target.value),
            })
          }
        >
          {HOUR_OPTIONS.map((hour) => (
            <option key={`end-${hour}`} value={hour}>
              {hourOptionLabel(hour)}
            </option>
          ))}
        </select>
        <span className="business-hour-tz"> CST</span>
      </span>
    );
  }

  return (
    <input
      className="rule-value"
      type="text"
      value={value ?? ""}
      onChange={(event) => handleOnChange(event.target.value)}
    />
  );
};

const EmailOperatorSelector = (props: any) => {
  const { field, options, value, handleOnChange, className, title } = props;
  if (field === BUSINESS_HOUR_FIELD) {
    return null;
  }

  return (
    <select
      className={className}
      title={title}
      value={value}
      onChange={(event) => handleOnChange(event.target.value)}
    >
      {options.map((option: { name: string; label: string }) => (
        <option key={option.name} value={option.name}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

const RuleGenerator = () => {
  const initialQuery: RuleGroupType = { combinator: "and", rules: [] };
  const [rules, setRules] = useState<any>([]);
  const [showRuleJson, setShowRuleJson] = useState<RuleGroupType>(initialQuery);
  const [assetRules, setAssetRules] = useState<RulesData>([]);
  const [assetType, setAssetType] = useState();
  const [assetTypeForRules, setAssetTypeForRules] = useState([]);
  const [prerequisites, setPrerequisites] =
    useState<RuleGroupType>(initialQuery);
  const [conditions, setConditions] = useState<RuleGroupType>(initialQuery);

  const [postConditions, setPostConditions] =
    useState<RuleGroupType>(initialQuery);
  const [defaultConditions, setDefaultConditions] = useState<any>([]);

  const [email, setEmail] = useState<RuleGroupType>(initialQuery);
  const [code, setCode] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [status, setStatus] = useState("");
  const [deployStatus, setDeployStatus] = useState("");
  const [normalAlert, setNormalAlert] = useState("");
  const [description, setDescription] = useState("");
  const [summary, setSummary] = useState("");
  const [recommendedAction, setRecommendedAction] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saveJsonErrorData, setSaveJsonErrorData] = useState("");
  const [ruleJsonErrorData, setRuleJsonErrorData] = useState<any>(null);
  const [ruleJsonModalData, setRuleJsonModalData] = useState<any>(null);
  const [downloadErrorData, setDownloadErrorData] = useState("");
  const [assets, setAssets] = useState<any[]>([]);
  const [ruleStatistics, setRuleStatistics] = useState({
    total: 0,
    enabled: 0,
    disabled: 0,
    internal: 0,
    redStatus: 0,
    yellowStatus: 0
  });

  const handleChange = (event: any) => {
    if (!event.target.value) return;
    setAssetType(event.target.value);
    getComponentsType(assetTypeForRules, event.target.value);
    
    // close the rule modal
    setIsModalOpen(false);

    // update the rule list
    fetchRules(event.target.value);
    
    // fetch assets for the selected asset type
    fetchAssetsByAssetType(event.target.value);
  };

  const fetchRules = async (assetType: any) => {
    if (!assetType) return;
    try {
      const yamlText = await ruleGeneratorService.getAssetTypeRules(assetType);

      // if no rules found
      if (yamlText === "") {
        setAssetRules({ anchors: [], rules: [] });
        setRules([]);
        calculateAndSetStatistics([]);
        return;
      }

      const rules = parseDocument(yamlText);
      // console.log("rules: ", rules)
      const jsonRules = yamlToJsonNew(assetType, rules);
      // console.log("jsonRules NEW: ", jsonRules)
      setAssetRules(jsonRules);
      setRules(jsonRules.rules);
      calculateAndSetStatistics(jsonRules.rules);
    } catch (err: any) {
      console.log(err);
      return
    }
  };

  const fetchAssetsByAssetType = async (assetType: string) => {
    if (!assetType) {
      setAssets([]);
      return;
    }
    try {
      const data = await ruleGeneratorService.getAssetsByAssetType(assetType);
      setAssets(data.assets || []);
    } catch (err) {
      console.error("Error fetching assets:", err);
      setAssets([]);
    }
  };

  const calculateAndSetStatistics = (rulesArray: any[]) => {
    const stats = {
      total: rulesArray.length,
      enabled: 0,
      disabled: 0,
      internal: 0,
      redStatus: 0,
      yellowStatus: 0
    };

    rulesArray.forEach((rule: any) => {
      const deploy = rule.deploy || 'enable';
      if (deploy === 'enable') {
        stats.enabled++;
      } else if (deploy === 'disable') {
        stats.disabled++;
      } else if (deploy === 'internal') {
        stats.internal++;
      }

      // Count by status
      const status = rule.status || '';
      if (status === 'red' || status === 'darkred') {
        stats.redStatus++;
      } else if (status === 'yellow') {
        stats.yellowStatus++;
      }
    });

    setRuleStatistics(stats);
  };

  const getAssetType = async () => {
    try {
      const data = await ruleGeneratorService.getAssetTypes();
      const transformedArray: any = Object.keys(data).map((key, index) => ({
        id: index + 1,
        name: key,
        display_name: data[key].display_name,
        components: data[key].components || [],
      }));
      setAssetTypeForRules(transformedArray);
      setAssetType(transformedArray[0].name);
      fetchRules(transformedArray[0].name);
      await getComponentsType(transformedArray, transformedArray[0].name);
      fetchAssetsByAssetType(transformedArray[0].name);
      return transformedArray;
    } catch (err) {
      console.error("Error in getAssetType:", err);
      return [];
    }
  };

  const getComponentsType = async (transformedArray: any, assetType: string) => {
    let selected_asset_type: any = transformedArray[0];
    for (let index = 0; index < transformedArray.length; index++) {
      const item: any = transformedArray[index];
      if (item.name == assetType) {
        selected_asset_type = item;
        break;
      }
    }

    var selectedAssetTypeMap: any = {};
    for (var i = 0; i < selected_asset_type.components.length; i++) {
      selectedAssetTypeMap[selected_asset_type.components[i].name] =
        selected_asset_type.components[i].count;
    }

    try {
      const componentData = await ruleGeneratorService.getComponentTypes();

      var components = [];
      for (var i = 0; i < componentData.length; i++) {
        var component = componentData[i];
        if (component.name in selectedAssetTypeMap) {
          component.count = selectedAssetTypeMap[component.name];
          components.push(component);
        }
      }

      let defaultConditionFields = [];
      for (var i = 0; i < components.length; i++) {
        var component = components[i];
        var sensors = component.sensors;
        var name = component.name;
        for (var j = 0; j < sensors.length; j++) {
          // TODO: Ensure name does not contains '-' within
          var n = name + "." + sensors[j].name;
          var l = name + " - " + sensors[j].name;
          defaultConditionFields.push({
            name: n,
            count: component.count,
            label: l,
          });
        }
      }

      setDefaultConditions(defaultConditionFields);
      return defaultConditionFields;
    } catch (err) {
      console.error("Error in getComponentsType:", err);
      return [];
    }
  };

  useEffect(() => {
    getAssetType();
  }, []);

  const handleShowRules = () => {
    Utility.showModal("showRuleJSON");
  };

  const onUpdateRuledata = async (item: any) => {
    setIsModalOpen(!isModalOpen);
    setCode(item?.code || "");
    setBackupCode(item?.code || "");
    setStatus(item?.status || "");
    setDeployStatus(item?.deploy || "");
    setNormalAlert(Boolean(item?.back_to_normal_alert) ? "True" : "False");
    setDescription(item?.description || "");
    setSummary(item?.summary || "-");
    setRecommendedAction(item?.recommended_action || "");
    setPrerequisites(initialQuery);
    setConditions(initialQuery);
    setPostConditions(initialQuery);
    setEmail(initialQuery);

    // Post Condition
    if (item?.trigger_post) {
      setPostConditions(convertToQueryBuilderFormat(item?.trigger_post));
    }

    // Condition
    if (item?.trigger_pre) {
      setConditions(convertToQueryBuilderFormat(item?.trigger_pre));
    }

    // Prerequisite
    if (item?.prerequisite) {
      setPrerequisites(convertToQueryBuilderFormat(item?.prerequisite));
    }

    // Email Condition
    if (item?.email_condition) {
      setEmail(
        collapseBusinessHourWindow(
          convertToQueryBuilderFormat(item?.email_condition)
        )
      );
    }
  };

  const getFieldsFromItem = (conditions: any, defaultFields: any[]) => {
    const fieldsSet = new Set<string>();

    (conditions?.rules ?? []).forEach((condition: any) => {
      if (typeof condition?.field === "string") {
        const field = condition.field;
        if (field) fieldsSet.add(field);
      }
    });

    const extractedFields = Array.from(fieldsSet).map((field) => {
      let val = {
        name: field,
        label: field.replace(".", " - "),
        count: 0
      };

      for (let index = 0; index < defaultFields.length; index++) {
        const item: any = defaultFields[index];
        if (item.name == field) {
          val.label = item.label;
          val.count = item.count;
          break;
        }
      }

      return val;
    });

    // Combine default and extracted fields, removing duplicates
    const allFieldsMap = new Map(
      [...defaultFields, ...extractedFields].map((field) => [field.name, field])
    );

    return Array.from(allFieldsMap.values());
  };

  const postConditionFields = getFieldsFromItem(
    postConditions ?? {},
    defaultPostConditionFields
  );
  const conditionFields = getFieldsFromItem(
    conditions ?? {},
    defaultConditions
  );
  const prerequisiteFields = getFieldsFromItem(
    prerequisites ?? {},
    defaultPrerequisiteFields
  );
  const emailFields = getFieldsFromItem(email ?? {}, defaultEmailFields);

  // Global focus manager
const FocusManager = {
  activeElementId: null as string | null,
  activeElementValue: null as string | null,
  activeElementSelectionStart: null as number | null,
  activeElementSelectionEnd: null as number | null,
  
  saveActiveElement: () => {
    const activeElement = document.activeElement as HTMLInputElement;
    if (activeElement && activeElement.tagName === 'INPUT' && activeElement.hasAttribute('data-focus-id')) {
      FocusManager.activeElementId = activeElement.getAttribute('data-focus-id');
      FocusManager.activeElementValue = activeElement.value;
      FocusManager.activeElementSelectionStart = activeElement.selectionStart;
      FocusManager.activeElementSelectionEnd = activeElement.selectionEnd;
    }
  },
  
  restoreFocus: () => {
    if (FocusManager.activeElementId) {
      setTimeout(() => {
        const element = document.querySelector(`[data-focus-id="${FocusManager.activeElementId}"]`) as HTMLInputElement;
        if (element) {
          element.focus();
          
          if (FocusManager.activeElementValue !== null) {
            element.value = FocusManager.activeElementValue;
          }
          
          if (FocusManager.activeElementSelectionStart !== null && 
              FocusManager.activeElementSelectionEnd !== null) {
            element.setSelectionRange(
              FocusManager.activeElementSelectionStart, 
              FocusManager.activeElementSelectionEnd
            );
          }
        }
      }, 0);
    }
  }
};

// Generate a unique ID for each component instance
let uniqueIdCounter = 0;
const generateUniqueId = () => {
  uniqueIdCounter++;
  return `custom-value-editor-${uniqueIdCounter}`;
};

const CustomValueEditor: ValueEditorProps['component'] = (props) => {
  const { value, handleOnChange, operator, field, rule } = props;
  
  // Generate a unique ID for this instance
  const [uniqueId] = useState(() => generateUniqueId());
  
  // Parse initial values from the incoming value prop
  let initialType: 'value' | 'field' | 'parameter' = 'value';
  let initialSelectedField = '';
  let initialArithmeticOperator = '';
  let initialInputValue = value !== undefined && value !== null ? value.toString() : '';
  
  // Check if it's a parameter
  if (typeof value === 'string' && value.startsWith('_parameter.')) {
    initialType = 'parameter';
    initialInputValue = value.replace('_parameter.', '');
  }
  // Check if it's a field reference
  else if (typeof value === 'string' && value.startsWith('$')) {
    initialType = 'field';
    const parts = value.replace(/^\$/, '').split(' ');
    initialSelectedField = parts[0] || '';
    initialArithmeticOperator = parts[1] || '';
    initialInputValue = parts.length > 2 ? parts[2] : '';
    // Ensure "0" is properly displayed
    if (initialInputValue === '0') {
      initialInputValue = '0';
    }
  }
  
  // Initialize state with parsed values
  const [type, setType] = useState<'value' | 'field' | 'parameter'>(initialType);
  const [arithmeticOperator, setArithmeticOperator] = useState<string>(initialArithmeticOperator);
  const [selectedField, setSelectedField] = useState<string>(initialSelectedField);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Add event listeners for focus and blur
  useEffect(() => {
    const handleFocus = () => {
      FocusManager.saveActiveElement();
    };
    
    const handleBlur = () => {
      setTimeout(() => {
        if (FocusManager.activeElementId === uniqueId) {
          FocusManager.saveActiveElement();
        }
      }, 10);
    };
    
    document.addEventListener('focus', handleFocus, true);
    document.addEventListener('blur', handleBlur, true);
    
    return () => {
      document.removeEventListener('focus', handleFocus, true);
      document.removeEventListener('blur', handleBlur, true);
    };
  }, [uniqueId]);
  
  // When component mounts, check if we need to restore focus
  useEffect(() => {
    if (FocusManager.activeElementId === uniqueId) {
      FocusManager.restoreFocus();
    }
  }, [uniqueId]);
  
  // When component unmounts, save state if we have focus
  useEffect(() => {
    return () => {
      if (document.activeElement === inputRef.current) {
        FocusManager.saveActiveElement();
      }
    };
  }, []);

  // Update input value when prop changes
  useEffect(() => {
    if (inputRef.current && (FocusManager.activeElementId !== uniqueId)) {
      if (value === 0 || value === '0') {
        inputRef.current.value = '0';
      } else if (type === 'value') {
        // Only update the value if we're not focused
        const stringValue = value !== undefined && value !== null ? value.toString() : '';
        if (inputRef.current.value !== stringValue) {
          inputRef.current.value = stringValue;
        }
      } else if (type === 'parameter' && typeof value === 'string' && value.startsWith('_parameter.')) {
        inputRef.current.value = value.replace('_parameter.', '');
      }
    }
  }, [value, type, uniqueId]);
  
  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as 'value' | 'field' | 'parameter';
    setType(newType);
    
    // Construct the value based on type
    const currentInputValue = inputRef.current?.value || '';
    if (newType === 'value') {
      handleOnChange(currentInputValue);
    } else if (newType === 'field') {
      handleOnChange(`$${selectedField} ${arithmeticOperator} ${currentInputValue}`);
    } else if (newType === 'parameter') {
      handleOnChange(`_parameter.${currentInputValue}`);
    }
  };

  const handleFieldChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newField = e.target.value;
    setSelectedField(newField);
    handleOnChange(`$${newField} ${arithmeticOperator} ${inputRef.current?.value || ''}`);
  };

  const handleArithmeticOperatorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newOperator = e.target.value;
    setArithmeticOperator(newOperator);
    handleOnChange(`$${selectedField} ${newOperator} ${inputRef.current?.value || ''}`);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    // Save focus state explicitly
    FocusManager.activeElementId = uniqueId;
    FocusManager.activeElementValue = newValue;
    FocusManager.activeElementSelectionStart = e.target.selectionStart;
    FocusManager.activeElementSelectionEnd = e.target.selectionEnd;
    
    if (type === 'value') {
      handleOnChange(newValue);
    } else if (type === 'field') {
      handleOnChange(`$${selectedField} ${arithmeticOperator} ${newValue}`);
    } else if (type === 'parameter') {
      handleOnChange(`_parameter.${newValue}`);
    }
    
    setTimeout(() => {
      FocusManager.restoreFocus();
    }, 10);
  };

  return (
    <>
      <select className="rule-value-option" value={type} onChange={handleTypeChange}>
        <option value="value">Value</option>
        <option value="field">Field</option>
        <option value="parameter">Parameter</option>
      </select>

      {type === 'field' && (
        <>
          {/* Field Dropdown */}
          <select className="rule-fields" value={selectedField} onChange={handleFieldChange}>
            <option value="">Select Field</option>
            {conditionFields.map((f) => (
              !f.label.includes(':seq=') &&
            <option key={f.name} value={f.name}>
              {f.label}
            </option>
          ))}
        </select>

          {/* Arithmetic Operator Dropdown */}
          <select className="rule-operators" value={arithmeticOperator} onChange={handleArithmeticOperatorChange}>
            <option value="+">+</option>
            <option value="-">-</option>
            <option value="*">*</option>
            <option value="/">/</option>
          </select>
        </>
      )}

      <input
        ref={inputRef}
        className="rule-value"
        data-focus-id={uniqueId}
        type="text"
        defaultValue={initialInputValue}
        onChange={handleInputChange}
      />
    </>
  );
};

  const CustomFieldEditor: React.FC<FieldSelectorProps> = (props) => {
    const { value, options, handleOnChange, title, className, rule } = props;
    
    // Parse the field name without sequence info for dropdown display
    const parseFieldName = (fieldWithSeq: string | undefined) => {
      if (!fieldWithSeq) return '';
      return fieldWithSeq.includes(':seq=') ? fieldWithSeq.split(':seq=')[0] : fieldWithSeq;
    };
    
    const parseSequence = (fieldWithSeq: string | undefined) => {
      if (!fieldWithSeq || !fieldWithSeq.includes(':seq=')) return '0';
      return fieldWithSeq.split(':seq=')[1];
    };
    
    const [fieldValue, setFieldValue] = useState<string>(parseFieldName(value));
    const [sequenceValue, setSequenceValue] = useState<string>(parseSequence(value));
    
    useEffect(() => {
      if (value !== undefined) {
        setFieldValue(parseFieldName(value));
        setSequenceValue(parseSequence(value));
      }
    }, [value]);
    
    const getCurrentField = () => {
      return options.find(opt => opt.name === fieldValue);
    };
    
    const shouldShowSequence = () => {
      const fieldObj = getCurrentField();
      return fieldObj && fieldObj.count !== undefined && fieldObj.count > 1;
    };
    
    // Generate sequence options based on field count
    const getSequenceOptions = () => {
      const fieldObj = getCurrentField();
      if (!fieldObj || fieldObj.count === undefined || fieldObj.count <= 1) {
        return ['1'];
      }
      
      return Array.from({ length: fieldObj.count }, (_, i) => String(i + 1));
    };
    
    const handleFieldChange = (newFieldValue: string) => {
      setFieldValue(newFieldValue);
      
      // Get the field object to check if we should show sequence
      const fieldObj = options.find(opt => opt.name === newFieldValue);
      if (!fieldObj) return;
      
      const newSequenceValue = '0';
      setSequenceValue(newSequenceValue);
      
      if (fieldObj.count !== undefined && fieldObj.count > 1) {
        const combinedValue = `${newFieldValue}:seq=${newSequenceValue}`;
        handleOnChange(combinedValue);
      } else {
        handleOnChange(newFieldValue);
      }
    };
    
    const handleSequenceChange = (newSequenceValue: string) => {
      setSequenceValue(newSequenceValue);
      
      if (!fieldValue || !shouldShowSequence()) return;
      
      const combinedValue = `${fieldValue}:seq=${newSequenceValue}`;      
      handleOnChange(combinedValue);
    };

    const sequenceOptions = getSequenceOptions();

    
    return (
      <>
        {/* Field dropdown from options */}
        <select
          value={fieldValue}
          onChange={(e) => handleFieldChange(e.target.value)}
          className={className}
          title={title}
        >
          {options.map((option) => (
            !option.label.includes(':seq=') &&
            <option key={option.name} value={option.name}>
              {option.label}
            </option>
          ))}
        </select>
        
        {/* Sequence dropdown */}
        {shouldShowSequence() && (
          <select
            value={sequenceValue}
            onChange={(e) => handleSequenceChange(e.target.value)}
            className="rule-seq"
          >
            <option value="0">Any</option>
            {sequenceOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )}
      </>
    );
  };
  
  const [ruleHistory, setRuleHistory] = useState<Record<string, any>>({});

  const handleQueryChange = (query: RuleGroupType) => {
    const newRuleHistory = { ...ruleHistory };
    
    // Process the query and track rule state
    const processRules = (rules: any[]) => {
      return rules.map(rule => {
        // If this is a rule group, process its rules
        if (rule.rules) {
          const processedGroup = {
            ...rule,
            rules: processRules(rule.rules)
          };
          return processedGroup;
        }
        
        const ruleId = rule.id;
        if (ruleId && newRuleHistory[ruleId]) {
          const prevRule = newRuleHistory[ruleId];
          
          // If field changed but includes sequence, preserve operator and value
          if (rule.field && rule.field.includes(':seq=') && 
              prevRule.field && rule.field.split(':seq=')[0] === prevRule.field.split(':seq=')[0]) {
          }
        }
        
        // Update history with current rule state
        if (ruleId) {
          newRuleHistory[ruleId] = { ...rule };
        }
        
        return rule;
      });
    };
    
    processRules(query.rules);
    setRuleHistory(newRuleHistory);
    setConditions(query);
  }
  
  async function saveJsonRule() {
    // Check for empty code
    if (code.trim() === "") {
      setSaveJsonErrorData(`Rule Name / Code cannot be Empty!`)
      Utility.showModal("saveRuleError");
      return
    }

    // Handle add new Rule name / Code uniqueness
    if (backupCode === "") {
      let codeExist = false
      if (assetRules && assetRules.rules) {
        for (let i = 0; i < assetRules.rules.length; i++) {
          if (assetRules.rules[i].code === code) {
            codeExist = true;
            break;
          }
        }
      }

      if (codeExist) {
        setSaveJsonErrorData(`Rule Name / Code already Exist: ${code}`)
        Utility.showModal("saveRuleError");
        return
      }
    }

    let rule: any = {
      code: code,
      status: status,
      deploy: deployStatus || "enable",
      back_to_normal_alert: normalAlert == "True" ? true : false || false,
      description: description,
      summary: summary,
      recommended_action: recommendedAction,
      prerequisite: prerequisites,
      trigger_pre: conditions,
      trigger_post: postConditions,
      email_condition: email,
    };

    if (rule.prerequisite && rule.prerequisite.rules.length > 0) {
      const result = formatRules(rule.prerequisite);
      if (result === null) {
        setRuleJsonErrorData({
          message: "Error in Rule Prerequisite", 
          rule: rule.prerequisite
        });
        Utility.showModal("ruleJSONError");
        return
      } 
      rule.prerequisite = result;
    } else {
      delete rule.prerequisite;
    }

    if (rule.trigger_pre && rule.trigger_pre.rules.length > 0) {
      const result = formatRules(rule.trigger_pre);
      if (result === null) {
        setRuleJsonErrorData({
          message: "Error in Rule Condition", 
          rule: rule.trigger_pre
        });
        Utility.showModal("ruleJSONError");
        return
      } 
      rule.trigger_pre = result;
    } else {
      delete rule.trigger_pre;
    }

    if (rule.trigger_post && rule.trigger_post.rules.length > 0) {
      const result = formatRules(rule.trigger_post);
      if (result === null) {
        setRuleJsonErrorData({
          message: "Error in Rule Post Condition", 
          rule: rule.trigger_post
        });
        Utility.showModal("ruleJSONError");
        return
      } 
      rule.trigger_post = result;
    } else {
      delete rule.trigger_post;
    }

    if (rule.email_condition && rule.email_condition.rules.length > 0) {
      const result = formatRules(expandEmailBusinessHours(rule.email_condition));
      if (result === null) {
        setRuleJsonErrorData({
          message: "Error in Rule Email Condition", 
          rule: rule.email_condition
        });
        Utility.showModal("ruleJSONError");
        return
      } 
      rule.email_condition = result;
    } else {
      delete rule.email_condition;
    }

    let found = false;
    if (assetRules && assetRules.rules) {
      for (let i = 0; i < assetRules.rules.length; i++) {
        if (backupCode !== "" && assetRules.rules[i].code === backupCode) {
          const link = assetRules.rules[i].link;
          assetRules.rules[i] = rule;
          assetRules.rules[i].link = link;
          found = true;
          break;
        }
      }
    }

    if (!found) {
      setRules((prevRules: any) => 
        prevRules ? [...prevRules, rule] : [rule]
      );

      setAssetRules(prev => ({
        ...prev,
        rules: prev?.rules ? [...prev.rules, rule] : [rule]
      }));    
    }

    setRuleJsonModalData(rule);
    Utility.showModal("ruleJSON");
    return assetRules;
  }

  async function saveRulesToFile() {
    const assetTypeRules = await saveAssetRules(assetRules);
    try {
      await ruleGeneratorService.updateAssetTypeRules(assetType, assetTypeRules.toString());
      // Recalculate statistics after saving (backend also updates, but we update UI immediately)
      calculateAndSetStatistics(assetRules.rules || []);
      Utility.showModal("saveRulesModal");
    } catch (err) {
      console.log("Error Asset Post: ", err);
    }
  }

  function addRuleModal() {
    setIsModalOpen(!isModalOpen);

    // Revert the state variables to it's defaults 
    setCode("");
    setBackupCode("");
    setStatus("");
    setDeployStatus("enable");
    setNormalAlert("False");
    setDescription("");
    setSummary("");
    setRecommendedAction("");
    setPrerequisites(initialQuery);
    setConditions(initialQuery);
    setPostConditions(initialQuery);
    setEmail(initialQuery);
  }

  function onDeleteRule(item: any, index: number): void {
    try {
      const updatedRules = [...rules];
      updatedRules.splice(index, 1);

      setRules(updatedRules);
      setAssetRules((prev) => ({
        ...prev,
        rules: updatedRules,
      }));
      // Recalculate statistics after deletion
      calculateAndSetStatistics(updatedRules);
    } catch (err) {
      console.log("ERROR On Delete Rule: ", err);
    }
  }

  const handleDownloadRules = async () => {
    if (!assetType) {
      return;
    }
    try {
      const yamlText = await ruleGeneratorService.getAssetTypeRules(assetType);
      
      if (yamlText === "") {
        setDownloadErrorData("No rules found for this asset type");
        Utility.showModal("downloadRuleError");
        return;
      }

      const blob = new Blob([yamlText], { type: "application/yaml" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `rules_${assetType}.yml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.log("Error downloading rules: ", err);
      setDownloadErrorData("Failed to download rules file");
      Utility.showModal("downloadRuleError");
    }
  };

  return (
    <div className="rule-generator-page">
      {/* Header */}
      <div
        className="bs-docs-header"
        id="content"
        style={{ background: "linear-gradient(135deg, #67ABB2, #4F8C92)" }}
      >
        <div className="container">
          <h1>Mechasense Rule Generator</h1>
        </div>
      </div>

      <div className="rg-docs-container">
        <div className="row-rg">
          {/* Asset Type Selection */}
          <div className="row" style={{ marginBottom: "20px", alignItems: "flex-end" }}>
            <div className="col-md-9" style={{ display: "flex", flexDirection: "column" }}>
              <label htmlFor="asset_type" className="form-label">
                Asset Type
              </label>
              <div>
                <select
                  className="form-select"
                  id="asset_type"
                  aria-label="Select asset type"
                  value={assetType || ""}
                  onChange={handleChange}
                >
                  {assetTypeForRules?.map((item: any, index: any) => (
                    <option key={index} value={item.name}>
                      {item.display_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-md-3" style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                className="btn btn-primary"
                onClick={handleDownloadRules}
                disabled={!assetType}
              >
                Download Rules
              </button>
            </div>
          </div>

          {/* Assets and Statistics Section */}
          {assetType && (
            <div className="row" style={{ marginBottom: "2px" }}>
              <div className="col-md-12">
                <div className="card" style={{ paddingTop: "15px" }}>
                  <div className="row" style={{ marginBottom: "15px" }}>
                    <div className="col-md-6">
                      <h5 style={{ margin: 0 }}>Asset Information</h5>
                    </div>
                    <div className="col-md-6">
                      <h5 style={{ margin: 0 }}>Rule Statistics</h5>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div style={{ marginBottom: "10px" }}>
                        <strong>Total Assets: {assets.length}</strong>
                      </div>
                      <div
                        style={{
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          padding: "10px",
                          maxHeight: "200px",
                          overflowY: "auto",
                          backgroundColor: "#f9f9f9"
                        }}
                      >
                        {assets.length > 0 ? (
                          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                            {assets.map((asset: any, index: number) => (
                              <li
                                key={index}
                                style={{
                                  padding: "5px 0",
                                  borderBottom: index < assets.length - 1 ? "1px solid #eee" : "none"
                                }}
                              >
                                {asset.asset_name || asset.asset_id || `Asset ${index + 1}`}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div style={{ color: "#999", fontStyle: "italic" }}>
                            No assets found for this asset type
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div style={{ marginTop: "10px" }}>
                        <div className="row" style={{ marginBottom: "10px" }}>
                          <div className="col-md-6" style={{ paddingRight: "5px" }}>
                            <label className="form-label" style={{ marginBottom: "5px", fontSize: "14px" }}>
                              Total Rules
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={ruleStatistics.total}
                              disabled
                              readOnly
                            />
                          </div>
                          <div className="col-md-6" style={{ paddingLeft: "5px" }}>
                            <label className="form-label" style={{ marginBottom: "5px", fontSize: "14px" }}>
                              Enabled
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={ruleStatistics.enabled}
                              disabled
                              readOnly
                            />
                          </div>
                        </div>
                        <div className="row" style={{ marginBottom: "10px" }}>
                          <div className="col-md-6" style={{ paddingRight: "5px" }}>
                            <label className="form-label" style={{ marginBottom: "5px", fontSize: "14px" }}>
                              Disabled
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={ruleStatistics.disabled}
                              disabled
                              readOnly
                            />
                          </div>
                          <div className="col-md-6" style={{ paddingLeft: "5px" }}>
                            <label className="form-label" style={{ marginBottom: "5px", fontSize: "14px" }}>
                              Internal
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={ruleStatistics.internal}
                              disabled
                              readOnly
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6" style={{ paddingRight: "5px" }}>
                            <label className="form-label" style={{ marginBottom: "5px", fontSize: "14px" }}>
                              Red Status
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={ruleStatistics.redStatus}
                              disabled
                              readOnly
                            />
                          </div>
                          <div className="col-md-6" style={{ paddingLeft: "5px" }}>
                            <label className="form-label" style={{ marginBottom: "5px", fontSize: "14px" }}>
                              Yellow Status
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={ruleStatistics.yellowStatus}
                              disabled
                              readOnly
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rules Section */}
          <div className="" role="main">
            <section className="bs-docs-section clearfix">
              <h1 id="basic" className="page-header">
                Rules
              </h1>

              <div className="rule-row-12">
                <button
                  id=""
                  className="btn btn-primary"
                  onClick={async () => {
                    await saveRulesToFile();
                  }}
                >
                  Save Rules to File
                </button>
              </div>

              <div className="rule-row-12">
                <button
                  id="add-rule"
                  type="button"
                  className="btn btn-primary"
                  onClick={addRuleModal}
                >
                  Add Rule
                </button>
              </div>

              {/* Rule Modal */}
              {isModalOpen && (
                <div className="card">
                  <div className="mb-3">
                    <label htmlFor="rule_name" className="form-label">
                      Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="rule_name"
                      value={code}
                      placeholder="Example rule"
                      onChange={(e: any) => setCode(e.target.value)}
                    />
                  </div>

                  <div style={{ display: "flex", flexDirection: "row", gap: "20px" }}>
                    {/* Status Dropdown */}
                    <div style={{ display: "flex", flexDirection: "column", width: "34%" }}>
                      <label
                        htmlFor="rule_status"
                        className="form-label"
                        style={{ marginRight: "10px" }}
                      >
                        Status
                      </label>
                      <select
                        className="form-select"
                        id="rule_status"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <option>Select an option</option>
                        <option value="green">Green</option>
                        <option value="yellow">Yellow</option>
                        <option value="orange">Orange</option>
                        <option value="red">Red</option>
                        <option value="darkred">Dark Red</option>
                        <option value="purple">Purple</option>
                      </select>
                    </div>

                    {/* Deploy Status Dropdown */}
                    <div style={{ display: "flex", flexDirection: "column", width: "33%"  }}>
                      <label
                        htmlFor="deploy_status"
                        className="form-label"
                        style={{ marginRight: "10px" }}
                      >
                        Deploy Status
                      </label>
                      <select
                        className="form-select"
                        id="deploy_status"
                        value={deployStatus}
                        onChange={(e) => setDeployStatus(e.target.value)}
                      >
                        <option value="enable">Enable</option>
                        <option value="disable">Disable</option>
                        <option value="internal">Internal</option>
                      </select>
                    </div>
                    {/* Normal Alert Flag Dropdown */}
                    <div style={{ display: "flex", flexDirection: "column", width: "33%"  }}>
                      <label
                        htmlFor="normal_alert"
                        className="form-label"
                        style={{ marginRight: "10px" }}
                      >
                        Normal Alert
                      </label>
                      <select
                        className="form-select"
                        id="normal_alert"
                        value={normalAlert}
                        onChange={(e) => setNormalAlert(e.target.value)}
                      >
                        <option value="False">False</option>
                        <option value="True">True</option>
                      </select>
                    </div>
                  </div>
                  <p></p>
                  <p></p>
                  <div className="mb-3">
                    <label htmlFor="rule_description" className="form-label">
                      Description
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="rule_description"
                      placeholder="Describes an example rule."
                      value={description}
                      onChange={(e: any) => setDescription(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="rule_summary" className="form-label">
                      Summary
                    </label>
                    <textarea
                      style={{ height: 100 }}
                      className="form-control"
                      id="rule_summary"
                      placeholder="Provide a summary to understand the rule easily."
                      value={summary}
                      onChange={(e: any) => setSummary(e.target.value)}
                    ></textarea>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="rule_recommended_action" className="form-label">
                      Recommended Action
                    </label>
                    <textarea
                      style={{ height: 100 }}
                      className="form-control"
                      id="rule_recommended_action"
                      placeholder="Provide a Recommended Action for the rule."
                      value={recommendedAction}
                      onChange={(e: any) => setRecommendedAction(e.target.value)}
                    ></textarea>
                  </div>

                  <div className="rule-row-12">
                    <h3>Prerequisites</h3>
                    <QueryBuilder
                      fields={prerequisiteFields}
                      query={prerequisites}
                      operators={prerequisiteOperators}
                      onQueryChange={(query: RuleGroupType) =>
                        setPrerequisites(query)
                      }
                    />

                    <div className="btn-group">
                      <button
                        className="btn btn-warning"
                        data-target="prerequisites"
                        onClick={() => setPrerequisites(initialQuery)}
                      >
                        Reset
                      </button>
                      <button
                        className="btn btn-info"
                        data-target="prerequisites"
                      >
                        Example rule
                      </button>
                      <button
                        className="btn btn-primary"
                        data-target="prerequisites"
                        onClick={() => {
                          setShowRuleJson(prerequisites);
                          handleShowRules();
                        }}
                      >
                        Show rule
                      </button>
                    </div>
                  </div>
                  <p></p>
                  <div className="rule-row-12">
                    <h3>Conditions</h3>
                    <QueryBuilder
                      fields={conditionFields}
                      query={conditions}
                      operators={operators}
                      controlElements={{
                        fieldSelector: CustomFieldEditor,
                        valueEditor: CustomValueEditor,
                      }}
                      onQueryChange={handleQueryChange}
                      autoSelectField={false}
                      resetOnFieldChange={false}
                      resetOnOperatorChange={false}
                    />

                    <div className="btn-group">
                      <button
                        className="btn btn-warning"
                        data-target="conditions"
                        onClick={() => setConditions(initialQuery)}
                      >
                        Reset
                      </button>
                      <button className="btn btn-info" data-target="conditions">
                        Example rule
                      </button>
                      <button
                        className="btn btn-primary"
                        data-target="conditions"
                        onClick={() => {
                          setShowRuleJson(conditions);
                          handleShowRules();
                        }}
                      >
                        Show rule
                      </button>
                    </div>
                  </div>
                  <p></p>
                  <div className="rule-row-12">
                    <h3>Post Conditions</h3>
                    {/* <div id="builder-post_conditions"></div> */}
                    <QueryBuilder
                      fields={postConditionFields}
                      query={postConditions}
                      operators={postConditionOperators}
                      onQueryChange={setPostConditions}
                    />

                    <div className="btn-group">
                      <button
                        className="btn btn-warning"
                        data-target="post_conditions"
                        onClick={() => setPostConditions(initialQuery)}
                      >
                        Reset
                      </button>
                      <button
                        className="btn btn-info"
                        data-target="post_conditions"
                      >
                        Example rule
                      </button>
                      <button
                        className="btn btn-primary"
                        data-target="post_conditions"
                        onClick={() => {
                          setShowRuleJson(postConditions);
                          handleShowRules();
                        }}
                      >
                        Show rule
                      </button>
                    </div>
                  </div>
                  <p></p>
                  <div className="rule-row-12">
                    <h3>Email Conditions</h3>
                    {/* <div id="builder-email_conditions"></div> */}
                    <QueryBuilder
                      fields={emailFields}
                      query={email}
                      operators={emailOperators}
                      getOperators={(field: string) =>
                        field === BUSINESS_HOUR_FIELD
                          ? businessHourOperator
                          : emailOperators
                      }
                      getDefaultOperator={(field: string) =>
                        field === BUSINESS_HOUR_FIELD ? "between" : "=="
                      }
                      getDefaultValue={(ruleOrField: any) => {
                        const field =
                          typeof ruleOrField === "string"
                            ? ruleOrField
                            : ruleOrField?.field;
                        if (field === BUSINESS_HOUR_FIELD) {
                          return {
                            start: DEFAULT_BUSINESS_HOUR_START,
                            end: DEFAULT_BUSINESS_HOUR_END,
                          };
                        }
                        return "";
                      }}
                      controlElements={{
                        valueEditor: EmailValueEditor,
                        operatorSelector: EmailOperatorSelector,
                      }}
                      resetOnFieldChange={true}
                      onQueryChange={(query: any) => setEmail(query)}
                    />
                    <div className="btn-group">
                      <button
                        className="btn btn-warning"
                        data-target="email_conditions"
                        onClick={() => setEmail(initialQuery)}
                      >
                        Reset
                      </button>
                      <button
                        className="btn btn-success"
                        data-target="email_conditions"
                      >
                        Example rule
                      </button>
                      <button
                        className="btn btn-primary"
                        data-target="email_conditions"
                        onClick={() => {
                          setShowRuleJson(email);
                          handleShowRules();
                        }}
                      >
                        Show rule
                      </button>
                    </div>
                  </div>
                  <p></p>
                  <p></p>
                  <p></p>
                  <p></p>
                  <p></p>
                  <div className="rule-row-12">
                    <div id="builder-save">
                      <button
                        className="btn btn-info"
                        type="button"
                        data-toggle="collapse"
                        data-target="#rule-modal"
                        aria-expanded="false"
                        aria-controls="rule-modal"
                        onClick={async () => await saveJsonRule()}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Rules Table */}
              <div className="rg-docs-container">
                <table className="table table-striped border-success">
                  <thead style={{ backgroundColor: '#4F8C92', color: 'white' }}>
                    <tr>
                      <th>Code</th>
                      <th>Status (Color)</th>
                      <th>Deploy Status</th>
                      <th>Description</th>
                      <th>Summary</th>
                      <th>Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules?.length > 0 ? (
                      rules.map((item: any, index: number) => (
                          <tr key={index} style={{ cursor: "pointer" }}>
                            <td
                              className="rules-code"
                              onClick={() => onUpdateRuledata(item)}
                            >
                              {item?.code}
                            </td>
                            <td>{item?.status}</td>
                            <td>{item?.deploy || 'enable'}</td>
                            <td>{item?.description}</td>
                            <td>{item?.summary ? item?.summary : "-"}</td>
                            <td
                              style={{ cursor: "pointer" }}
                              onClick={() => onDeleteRule(item, index)}
                            >
                              <i className="fa fa-trash" aria-hidden="true"></i>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan={6}>No rules found</td></tr>
                      )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      </div>

      <div
        className="modal fade bd-example-modal-lg"
        id="showRuleJSON"
        role="dialog"
        aria-labelledby="myLargeModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog ">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel"></h5>
              <span
                aria-hidden="true"
                className="close close-modal"
                aria-label="Close"
                onClick={() => {
                  Utility.closeModal("showRuleJSON");
                }}
                style={{ cursor: "pointer" }}
              >
                &times;
              </span>
            </div>
            <div className="modal-body modal-text">
              <div
                style={{
                  maxHeight: "400px",
                  overflowY: "auto",
                  border: "1px solid #ccc",
                  padding: "8px",
                }}
              >
                {showRuleJson ? (
                  <pre className="whitespace-pre-wrap text-sm">
                    {JSON.stringify(showRuleJson, null, 2)}
                  </pre>
                ) : (
                  <p>No data available</p>
                )}
              </div>
            </div>
            <div className="modal-footer d-flex justify-content-end">
              <button
                className="btn btn-primary"
                style={{ backgroundColor: '#4F8C92', borderColor: '#4F8C92', color: 'white' }}
                onClick={() => {
                  Utility.closeModal("showRuleJSON");
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade bd-example-modal-lg"
        id="ruleJSON"
        role="dialog"
        aria-labelledby="myLargeModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog ">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel"></h5>
              <span
                aria-hidden="true"
                className="close close-modal"
                aria-label="Close"
                onClick={() => {
                  Utility.closeModal("ruleJSON");
                  setIsModalOpen(!isModalOpen);
                }}
                style={{ cursor: "pointer" }}
              >
                &times;
              </span>
            </div>
            <div className="modal-body modal-text">
              <div
                style={{
                  maxHeight: "400px",
                  overflowY: "auto",
                  border: "1px solid #ccc",
                  padding: "8px",
                }}
              >
                {ruleJsonModalData ? (
                  <pre className="whitespace-pre-wrap text-sm">
                    {JSON.stringify(ruleJsonModalData, null, 2)}
                  </pre>
                ) : (
                  <p>No data available</p>
                )}
              </div>
            </div>
            <div className="modal-footer d-flex justify-content-end">
              <button
                className="btn btn-primary"
                style={{ backgroundColor: '#4F8C92', borderColor: '#4F8C92', color: 'white' }}
                onClick={() => {
                  Utility.closeModal("ruleJSON");
                  setIsModalOpen(!isModalOpen);
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade bd-example-modal-lg"
        id="ruleJSONError"
        role="dialog"
        aria-labelledby="myLargeModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog ">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">{ruleJsonErrorData?.message || "Error In Rule: "}</h5>
              <span
                aria-hidden="true"
                className="close close-modal"
                aria-label="Close"
                onClick={() => {
                  Utility.closeModal("ruleJSONError");
                }}
                style={{ cursor: "pointer" }}
              >
                &times;
              </span>
            </div>
            <div className="modal-body modal-text">
              <div
                style={{
                  maxHeight: "400px",
                  overflowY: "auto",
                  border: "1px solid #ccc",
                  padding: "8px",
                }}
              >
                {ruleJsonErrorData?.rule ? (
                  <pre className="whitespace-pre-wrap text-sm">
                    {JSON.stringify(ruleJsonErrorData.rule, null, 2)}
                  </pre>
                ) : (
                  <p>No data available</p>
                )}
              </div>
            </div>
            <div className="modal-footer d-flex justify-content-end">
              <button
                className="btn btn-primary"
                style={{ backgroundColor: '#4F8C92', borderColor: '#4F8C92', color: 'white' }}
                onClick={() => {
                  Utility.closeModal("ruleJSONError");
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade bd-example-modal-lg"
        id="saveRuleError"
        role="dialog"
        aria-labelledby="myLargeModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog ">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">
                Add Rules
              </h5>
              <span
                aria-hidden="true"
                className="close close-modal"
                aria-label="Close"
                onClick={() => {
                  Utility.closeModal("saveRuleError");
                }}
                style={{ cursor: "pointer" }}
              >
                &times;
              </span>
            </div>
            <div className="modal-body modal-text">
              <p>{saveJsonErrorData || `Error In Rule: ${code}`}</p>
            </div>
            <div className="modal-footer d-flex justify-content-end">
              <button
                className="btn btn-primary"
                style={{ backgroundColor: '#4F8C92', borderColor: '#4F8C92', color: 'white' }}
                onClick={() => {
                  Utility.closeModal("saveRuleError");
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade bd-example-modal-lg"
        id="saveRulesModal"
        role="dialog"
        aria-labelledby="myLargeModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog ">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">
                Save Rules
              </h5>
              <span
                aria-hidden="true"
                className="close close-modal"
                aria-label="Close"
                onClick={() => {
                  Utility.closeModal("saveRulesModal");
                }}
                style={{ cursor: "pointer" }}
              >
                &times;
              </span>
            </div>
            <div className="modal-body modal-text">
              <p>Rules Saved to File</p>
            </div>
            <div className="modal-footer d-flex justify-content-end">
              <button
                className="btn btn-primary"
                style={{ backgroundColor: '#4F8C92', borderColor: '#4F8C92', color: 'white' }}
                onClick={() => {
                  Utility.closeModal("saveRulesModal");
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade bd-example-modal-lg"
        id="downloadRuleError"
        role="dialog"
        aria-labelledby="myLargeModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog ">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">
                Download Error
              </h5>
              <span
                aria-hidden="true"
                className="close close-modal"
                aria-label="Close"
                onClick={() => {
                  Utility.closeModal("downloadRuleError");
                }}
                style={{ cursor: "pointer" }}
              >
                &times;
              </span>
            </div>
            <div className="modal-body modal-text">
              <p>{downloadErrorData || "Failed to download rules file"}</p>
            </div>
            <div className="modal-footer d-flex justify-content-end">
              <button
                className="btn btn-primary"
                style={{ backgroundColor: '#4F8C92', borderColor: '#4F8C92', color: 'white' }}
                onClick={() => {
                  Utility.closeModal("downloadRuleError");
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RuleGenerator;